import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { axios } from "@axios";
import storage from "@utils/storage";
import { downloadTransactionReceipt } from "@utils/generateTransactionReceipt";
import { ANONYMOUS_LENDER_LABEL } from "@utils/lenderDisplay";

const ViewTransaction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactionData } = location.state || {};
  const transactionId = transactionData?.uuid;
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const storedUser = storage.getUser();
  const userRole =
    storedUser?.role ?? storedUser?.user?.role ?? storedUser?.userType;
  const isBorrower = userRole === "borrower";

  useEffect(() => {
    if (!transactionId) {
      setError("Transaction ID not provided");
      setLoading(false);
      return;
    }

    const fetchTransaction = async () => {
      try {
        const response = await axios.get(
          `/transaction/get-transaction/${transactionId}`
        );
        setTransaction(response.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || "Failed to fetch transaction"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [transactionId]);

  const handleDownloadReceipt = () => {
    if (!transaction) return;
    setDownloading(true);
    try {
      downloadTransactionReceipt(transaction);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="loader">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  const {
    amount,
    status,
    transactionType,
    transactionDate,
    createdAt,
    remarks,
    paymentId,
    uuid,
    fromWallet,
    toWallet,
  } = transaction || {};

  const displayDate = transactionDate || createdAt;

  return (
    <div className="content completed-applicant-section">
      <div className="content-wrapper review-page">
        <div className="title-search-block d-flex align-items-center justify-content-between">
          <div
            className="back-btn mb-4"
            style={{
              width: "95%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button onClick={() => navigate(-1)} type="button" className="button-back">
              Back
            </button>
            <button
              type="button"
              className="send-contract-button"
              onClick={handleDownloadReceipt}
              disabled={downloading}
            >
              {downloading ? "Preparing..." : "Download Receipt (PDF)"}
            </button>
          </div>
        </div>

        <div className="outer-box-wrapper completed-offers p-0">
          <div className="settlement-box m-0">
            <h6 className="m-0 settlement-date">Transaction Details</h6>
            <div className="applicant-info">
              <div className="applicant-details">
                <div className="applicant-infofields mb-3">
                  Transaction ID: <span>{paymentId || uuid || "-"}</span>
                </div>
                <div className="applicant-infofields mb-3">
                  Amount: <span>£{Number(amount ?? 0).toFixed(2)}</span>
                </div>
                <div className="applicant-infofields mb-3">
                  Status: <span>{status}</span>
                </div>
                <div className="applicant-infofields mb-3">
                  Type: <span>{transactionType}</span>
                </div>
                <div className="applicant-infofields mb-3">
                  Transaction Date:{" "}
                  <span>
                    {displayDate
                      ? new Date(displayDate).toLocaleString("en-GB")
                      : "—"}
                  </span>
                </div>
                {remarks && (
                  <div className="applicant-infofields mb-3">
                    Remarks: <span>{remarks}</span>
                  </div>
                )}

                <h6 className="mt-4">
                  {isBorrower ? "Your Wallet" : "Borrower Details"}
                </h6>
                {isBorrower ? (
                  <div className="applicant-infofields mb-2">
                    Wallet Balance:{" "}
                    <span>
                      £{((fromWallet?.amount || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="applicant-infofields mb-2">
                      Name:{" "}
                      <span>
                        {fromWallet?.user
                          ? `${fromWallet.user.firstName} ${fromWallet.user.lastName}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="applicant-infofields mb-2">
                      Email: <span>{fromWallet?.user?.email || "N/A"}</span>
                    </div>
                    <div className="applicant-infofields mb-2">
                      Wallet Balance:{" "}
                      <span>
                        £{((fromWallet?.amount || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}

                <h6 className="mt-4">
                  {isBorrower ? "Counterparty" : "Your Wallet"}
                </h6>
                {isBorrower ? (
                  <div className="applicant-infofields mb-2">
                    Lender: <span>{ANONYMOUS_LENDER_LABEL}</span>
                  </div>
                ) : (
                  <>
                    <div className="applicant-infofields mb-2">
                      Name:{" "}
                      <span>
                        {toWallet?.user
                          ? `${toWallet.user.firstName} ${toWallet.user.lastName}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="applicant-infofields mb-2">
                      Email: <span>{toWallet?.user?.email || "N/A"}</span>
                    </div>
                    <div className="applicant-infofields mb-2">
                      Wallet Balance:{" "}
                      <span>£{((toWallet?.amount || 0) / 100).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTransaction;
